import React from "react";
import PolicyLayout from "../../../components/PolicyLayout";
import s from "../../../styles/PolicyLayout.module.css";

export default function BrainIqTestPrivacy() {
  return (
    <PolicyLayout
      title="privacy policy"
      appName="BrainIQ: Boost Your Mind"
      date="DECEMBER 2025"
    >
      <div className={s.title}>1) Introduction and Scope</div>
      <p>
        This Privacy Policy explains how “BrainIQ: Boost Your Mind”
        (the “App”) collects, uses, stores, and discloses information about
        users. The App offers entertainment-focused quizzes (“brain age,”
        memory, attention, personality) and daily one-minute exercises with
        local scoring and optional premium access. Core experiences work offline
        with an on-device cache, while synchronized features use Firebase. This
        Policy covers onboarding and optional sign-in, local quiz execution and
        scoring, JSON test content from assets or Firestore, paywall/entitlements,
        sharing, notifications, analytics and crash diagnostics, an optional AI
        result generator, and support. By using the App, you acknowledge this
        Policy and agree to the practices described.
      </p>

      <div className={s.title}>2) Controller and Contact Information</div>
      <p>
        The controller determining purposes and means of processing is Aleksandra
        Rakitina. For questions or to exercise rights (access, deletion,
        correction, restriction, objection, portability), contact
        alexwadek@gmail.com. We verify identity before fulfilling requests,
        typically by confirming control of the account email or requesting
        minimal additional details. Privacy correspondence may be retained for
        compliance and security where permitted by law.
      </p>

      <div className={s.title}>
        3) Key Principles (Entertainment-Only, Minimization, User Control)
      </div>
      <p>
        Entertainment-only: the App provides fun self-reflection quizzes and
        casual brain training; it is not a medical or psychological tool.
        Minimization: we collect only what is necessary to deliver features
        (auth, synchronized storage, subscriptions, reliability analytics,
        notifications). User control: you decide whether to sign in, which
        quizzes to take, whether to enable AI results, and when to delete
        history. We do not sell Personal Data, and we avoid behavioral
        advertising SDKs.
      </p>

      <div className={s.title}>4) Definitions</div>
      <p>
        “Personal Data” means information relating to an identified or
        identifiable person. “Quiz Data” includes tests you take, item
        responses, local scores, result bands, and timestamps. “Daily Game
        Data” covers daily exercise attempts and scores. “Content Config”
        refers to JSON test definitions stored in assets or retrieved from
        Firestore when enabled. “Processing” includes collection, storage,
        transformation, transmission, sharing, and deletion. “Service
        Providers/Processors” are third parties acting on our instructions
        (Firebase, RevenueCat, messaging, optional AI service).
      </p>

      <div className={s.title}>5) What We Collect</div>
      <p>
        Depending on your choices and device settings, we process: (a) Account
        data via Firebase Auth if you sign in (email, display name, provider
        identifiers) or a local guest identifier if you remain anonymous; (b)
        Quiz Data (test_id, response choices, local scale scores, result band);
        (c) Daily Game Data (game type, score, attempt timestamp, best-of-day);
        (d) Content Config flags from Remote Config and, when enabled, JSON
        test payloads from Firestore; (e) Subscription/entitlement metadata
        from RevenueCat (product IDs, trial eligibility, renewal outcome,
        expiration/cancellation); (f) Analytics and crash diagnostics (event
        counters, anonymized error codes, device/OS/app versions); (g)
        Notification tokens and preferences for push via Firebase Cloud
        Messaging. We do not receive payment card numbers.
      </p>

      <div className={s.title}>6) Sources of Data</div>
      <p>
        Most information originates from you when you take quizzes, use
        exercises, and manage settings. Additional data is generated
        automatically by the App or providers (analytics/crash diagnostics) to
        maintain stability. If Remote Config enables remote tests, Content
        Config may be fetched from Firestore. Optional AI results use a secured
        proxy that processes minimal aggregates.
      </p>

      <div className={s.title}>7) Offline-First Storage and Sync</div>
      <p>
        The App maintains an offline cache using Hive/SharedPrefs so quizzes and
        history work without a connection. When network connectivity is
        available and you are signed in (if you choose), select data may
        synchronize to Firestore to support restoration across devices. Guest
        usage stores data only on the device; deleting the App or resetting the
        device may permanently remove guest data.
      </p>

      <div className={s.title}>8) Test Engine, Scoring, and Result Display</div>
      <p>
        Quizzes run locally. Your answers are scored on-device against scale
        keys defined in the JSON for each test. Results are displayed as a
        title/band and short description mapped to score ranges. Depending on
        configuration, paywall gating may occur before the full result is
        shown. We store scores and result bands in local history; answer-level
        data can remain local unless remote sync is enabled for your account.
      </p>

      <div className={s.title}>9) Paywall and Premium Entitlements</div>
      <p>
        Subscriptions use platform billing via RevenueCat and may include weekly
        $4.99 (3-day trial), monthly $14.99, yearly $39.99 with placements
        before results and from the menu. We receive entitlement metadata
        (product identifiers, trial eligibility, renewal outcomes, expiration
        dates, cancellation status) to grant access to premium features (full
        reports, unlimited daily exercises, history depth, themes). Manage or
        cancel in device account settings; refunds and grace periods follow
        platform rules. We do not receive card numbers.
      </p>

      <div className={s.title}>10) Daily Training Games</div>
      <p>
        For retention and self-improvement motivation, daily games (memory
        sequence, odd-one-out, tap speed) record attempt timestamps and scores.
        The free tier may allow one attempt per day; premium unlocks unlimited
        sessions. Scores are stored locally and, if you are signed in and sync
        is enabled, may replicate to Firestore for backup and multi-device
        continuity.
      </p>

      <div className={s.title}>
        11) Optional AI Result Summaries (Feature-Flagged)
      </div>
      <p>
        If enabled by Remote Config, the App may send minimal aggregates (e.g.,
        test_id, total score or band, high-level pattern choices) to a secured
        proxy (Firebase Functions) that calls an AI model provider with our API
        key. The provider returns a friendly 120–160-word summary with general
        tips. We do not send full answer transcripts when aggregates suffice.
        Communications occur over HTTPS; providers are contractually required to
        secure data and act on our instructions. You can keep AI summaries off
        by not enabling this feature or when the flag is disabled by default.
      </p>

      <div className={s.title}>
        12) Push Notifications and Local Reminders
      </div>
      <p>
        With your permission, the App may send reminders such as “New daily
        brain exercise is ready,” streak nudges, or weekly check-ins. Delivery
        requires a device token used solely to route messages. You can disable
        notifications in system settings and, where provided, in-app toggles. We
        avoid placing sensitive quiz details in notification bodies.
      </p>

      <div className={s.title}>
        13) Analytics, Events, and Crash Diagnostics
      </div>
      <p>
        We use Firebase Analytics and Crashlytics to understand reliability and
        feature adoption—not to build third-party advertising profiles. Typical
        events: onboarding_complete, test_started &#123;test_id&#125;,
        test_finished &#123;test_id, score, band&#125;, paywall_shown
        &#123;placement&#125;, trial_started &#123;plan&#125;, purchase_completed
        &#123;plan&#125;, daily_game_started &#123;game&#125;, daily_game_result
        &#123;game, score&#125;. Where feasible, analytics are
        aggregated/pseudonymized and exclude full answer text. Platform privacy
        controls can reduce analytics, which may limit troubleshooting of rare
        device-specific issues.
      </p>

      <div className={s.title}>14) Sharing and Exports</div>
      <p>
        If sharing is offered (e.g., share a static result card), the App
        creates an image or text summary and uses the system share sheet.
        Exports go to destinations you choose (messages, email, social apps) and
        are governed by those services’ policies. Deleting an item in-app does
        not remove copies you already shared elsewhere.
      </p>

      <div className={s.title}>
        15) Remote Config and A/B Experiments
      </div>
      <p>
        We may use Firebase Remote Config to adjust non-personalized settings
        such as enabling remote tests, toggling AI summaries, changing copy on
        paywalls, or varying trial offers where allowed. These experiments use
        minimal identifiers and aggregated metrics to evaluate performance
        without storing your quiz contents for marketing.
      </p>

      <div className={s.title}>16) What We Do Not Collect or Sell</div>
      <p>
        We do not sell Personal Data. We do not collect payment card numbers. We
        do not request precise geolocation or access your contacts for core
        usage. We do not present your Quiz Data to third-party ad networks for
        profiling. We do not provide clinical assessments and do not collect
        health records.
      </p>

      <div className={s.title}>
        17) Legal Bases (EEA/UK) and Global Principles
      </div>
      <p>
        For EEA/UK users, legal bases include: performance of a contract (core
        quizzes, daily exercises, sync, subscriptions), legitimate interests
        (security, fraud prevention, product improvement) balanced against your
        rights through minimization and controls, consent (notifications,
        certain analytics categories, AI summaries when required), and legal
        obligations (records, lawful requests). If purposes materially change,
        we will update this Policy and, where required, seek additional consent.
      </p>

      <div className={s.title}>18) Security Measures</div>
      <p>
        We implement administrative, technical, and organizational safeguards:
        HTTPS for all provider communications; encryption at rest for
        Firestore/Cloud Storage; rules-based access control; least-privilege
        access to operational tools; environment separation; credential
        rotation; monitoring for abuse. No system is perfectly secure, but an
        offline-first design reduces exposure by keeping scoring and history
        local by default.
      </p>

      <div className={s.title}>19) Data Retention and Deletion</div>
      <p>
        Local history (tests taken, scores, bands; daily game scores) persists
        until you delete it or uninstall the App. If you use a signed-in account
        with sync, Firestore records remain until you delete them or request
        account deletion. Provider backups/replication may introduce short
        delays before full purge. Subscription/entitlement metadata may be
        retained longer for accounting and fraud prevention in pseudonymized or
        aggregated form. Crash/analytics logs are preserved for operational
        windows then de-identified or deleted.
      </p>

      <div className={s.title}>
        20) Children’s Privacy and Sensitive Uses
      </div>
      <p>
        The App is not directed to children, and we do not knowingly collect
        Personal Data from users under the age where parental consent is
        required by law. Results are for entertainment and self-reflection only
        and are not clinical assessments. If you believe a minor used the App
        without appropriate authorization, contact alexwadek@gmail.com and we
        will remove information and restrict access as appropriate.
      </p>

      <div className={s.title}>21) Your Rights and Choices</div>
      <p>
        Subject to law, you may have rights to access, rectify, delete, or port
        Personal Data; to restrict or object to certain processing; and to
        withdraw consent where processing relies on consent. In-app controls let
        you clear local history, disable notifications, and opt out of AI
        summaries (when configurable). Subscription management (including
        cancellation/refunds) occurs in platform account settings. To exercise
        other rights, email alexwadek@gmail.com from your sign-in address; we
        will verify identity and respond within a reasonable time.
      </p>

      <div className={s.title}>
        22) Do Not Track, Identifiers, and Cookies
      </div>
      <p>
        As a native app, we do not use browser cookies. SDKs may store
        identifiers to support authentication, sync, analytics, crash
        diagnostics, notifications, and subscriptions. These function similarly
        to cookies by enabling sessions, entitlement checks, and measurement.
        The App does not rely on advertising identifiers for targeted ads.
        Browser “Do Not Track” signals generally do not apply to native apps.
      </p>

      <div className={s.title}>
        23) Third-Party Providers and Disclosures
      </div>
      <p>
        We rely on vetted providers acting on our instructions: Firebase (Auth
        optional, Firestore, Remote Config, Analytics, Messaging, Crashlytics,
        Functions), RevenueCat (subscriptions), and an AI model provider
        accessed via a secure proxy when enabled. Providers must implement
        appropriate security and are prohibited from using Personal Data for
        independent marketing. We may disclose information when legally required
        by competent authorities after validating scope and jurisdiction.
      </p>

      <div className={s.title}>
        24) Accuracy, Product Limitations, and Health Disclaimer
      </div>
      <p>
        Quizzes and tips are for entertainment and self-reflection. Scores are
        based on self-report and simple interactions and can be affected by
        context, device, and random variation. Outputs may be inaccurate or
        incomplete. The App does not diagnose, treat, or provide medical or
        psychological advice. If you need professional guidance, consult
        qualified professionals.
      </p>

      <div className={s.title}>
        25) Fair Use, Abuse Prevention, and Limits
      </div>
      <p>
        To maintain reliability and control costs, we may apply fair-use
        thresholds (for example, excessive automated attempts or request bursts)
        and rate-limits for AI summaries when enabled. Attempts to bypass limits
        or misuse subscriptions may trigger protective measures such as
        temporary lockouts or entitlement checks. Material changes to limits
        will follow platform rules and applicable law.
      </p>

      <div className={s.title}>26) International Data Transfers</div>
      <p>
        Depending on your and our providers’ locations, data may be processed in
        jurisdictions with different data-protection rules. Where required, we
        rely on appropriate safeguards for cross-border transfers (contractual
        protections and platform mechanisms). Regardless of location, providers
        must implement security measures consistent with this Policy and
        applicable law.
      </p>

      <div className={s.title}>27) Changes to This Policy</div>
      <p>
        We may update this Policy to reflect operational, legal, or regulatory
        developments. The “Last Updated” date will change when updates occur.
        Material changes affecting your rights or our processing will be
        highlighted in-app; where required, we will request consent. Continued
        use of the App after changes take effect signifies acceptance.
      </p>

      <div className={s.title}>
        28) How to Submit Privacy Requests and Verification Steps
      </div>
      <p>
        To exercise your rights, email alexwadek@gmail.com and specify the
        request type (access, deletion, correction, restriction, objection,
        portability). Include helpful context such as device model and app
        version. We verify identity to prevent unauthorized access or deletion
        and may request limited additional information or a confirmation code.
        Where feasible, responses are provided in a portable, machine-readable
        format. We may decline or limit requests when permitted by law (for
        example, if fulfilling a request would compromise security, interfere
        with legal obligations, or infringe on others’ privacy).
      </p>
    </PolicyLayout>
  );
}
